/**
 * Disclaimer for Investor Relations
 */

/**
 * @typedef BlockedMessage
 * @param {string} de
 * @param {string} en
 *
 * @typedef CountryInvestorRelations
 * @param {boolean} blocked
 * @param {BlockedMessage} blockedMessage
 *
 * @typedef Country
 * @param {string} cioc
 * @param {CountryInvestorRelations} [investorRelations]
 */

class DisclaimerStep {
	/**
	 * @param {string} name
	 */
	constructor(name) {
		this.name = name;
	}

	/**
	 * @param {SubmitEvent} submitEvent
	 * @param {FormData} formData
	 * @returns {boolean} if true, proceeds to next step
	 */
	handleSubmit(submitEvent, formData) {
		return true;
	}
}

class CountryStep extends DisclaimerStep {
	/**
	 * @param {Country[]} countries
	 */
	constructor(countries) {
		super('country');
		this.countries = countries;
	}
	handleSubmit(submitEvent, formData) {
		const userCountry = formData.get('user-country');
		const countryItem = this.countries.find((c) => c.cioc === userCountry);
		if (!countryItem) {
			window.location.reload();
			return false;
		}

		setCookie('ir-disclaimer_country', countryItem.cioc);

		/**
		 * @var {Country} countryItem
		 */
		if (countryItem.investorRelations?.blocked === true) {
			let messages = [
				countryItem.investorRelations.blockedMessage?.de,
				countryItem.investorRelations.blockedMessage?.en,
			].filter((m) => typeof m === 'string' && m !== '').join("\r\n\r\n");

			if (!messages) {
				messages = "Das ist leider nicht möglich. \r\n\r\n" +
					"This is unfortunately not possible.";
			}

			alert(messages);

			return false;
		}

		return true;
	}
}

class AgreementStep extends DisclaimerStep {
	constructor() {
		super('agreement');
	}
}

class B2bStep extends DisclaimerStep {
	constructor() {
		super('b2b');
	}

	handleSubmit(submitEvent, formData) {
		if (!formData.get('b2b-accepted')) {
			alert(
				"Leider können wir Ihnen die Informationen nicht anzeigen. \r\n\r\n" +
				"We are unable to display this information.",
			);
			return false;
		}
		return true;
	}
}

class IrDisclaimer {
	/**
	 * @param {HTMLElement} rootEl
	 * @param {DisclaimerStep[]} steps
	 */
	constructor(rootEl, steps) {
		this.rootEl = rootEl;
		this.steps = steps;
		this.#addEventListeners();
	}

	#addEventListeners() {
		this.rootEl.addEventListener('submit', (event) => {
			event.preventDefault();
			const step = this.steps.find((step) => !!event.target.closest(`[data-disclaimer-step="${step.name}"]`));
			if (!step) return;
			const proceed = step.handleSubmit(event, new FormData(event.target));

			if (proceed) {
				this.#showNextStep(step.name);
			}
		});
	}

	/**
	 * @param {string} currentStepName
	 */
	#showNextStep(currentStepName)
	{
		const currentStepIndex = this.steps.findIndex((step) => step.name === currentStepName);
		const nextStep = this.steps[currentStepIndex + 1];

		if (!nextStep) {
			// all successful - reload
			setCookie('ir-disclaimer', '1');
			window.location.reload();
			return;
		}

		this.steps.forEach((step) => {
			const stepEl = this.rootEl.querySelector(`[data-disclaimer-step="${ step.name }"]`)
			if (!(stepEl instanceof HTMLElement)) return;
			if (step.name === nextStep.name) {
				stepEl.classList.remove('hide');
			} else {
				stepEl.classList.add('hide');
			}
		});
	}
}

export const initIrDisclaimer = () => {
	document.querySelectorAll('[data-location-disclaimer]').forEach((el) => {
		if (!(el instanceof HTMLElement)) return;
		const countries = JSON.parse(
			el.querySelector('script[data-countries]').textContent,
		);
		new IrDisclaimer(el, [
			new CountryStep(countries),
			new AgreementStep(),
			new B2bStep(),
		]);
	});
}
